import './polyfills/request-video-frame-callback';
import { STATE } from './camera-handler';
import { deepMerge } from '@soitron/global-helpers';
export class VideoDataStream {
    _cameraHandler;
    _requestVideoFrameCallbackId = null;
    _onDataStreamCallback = null;
    _dataStreamCanvas = null;
    _dataStreamCanvasContext = null;
    get __getConfig() {
        return Object.freeze(this._config);
    }
    get __getComputedROI() {
        return this._computeROI();
    }
    _config = {
        x: 0,
        y: 0,
        width: 300,
        height: 300,
        minOutputSize: 640,
        processingFrequency: 5,
        resize: false,
        resizeWidth: 640,
        resizeHeight: 640,
    };
    constructor(_cameraHandler, config) {
        this._cameraHandler = _cameraHandler;
        this.__updateConfig(config);
        this._setupDataStreamCanvas();
    }
    __onDataStream(callback) {
        if (this._onDataStreamCallback || !callback)
            return;
        this._onDataStreamCallback = callback;
    }
    __removeOnDataStream() {
        this._onDataStreamCallback = null;
    }
    __updateConfig(config) {
        if (config)
            config.minOutputSize = Math.max(config?.minOutputSize || 0, this._config.minOutputSize);
        this._config = deepMerge(this._config, config || {});
    }
    __start() {
        this._captureFrame(0, null);
    }
    __stop() {
        if (this._requestVideoFrameCallbackId !== null) {
            this._cameraHandler.__getVideoElement.cancelVideoFrameCallback(this._requestVideoFrameCallbackId);
            this._requestVideoFrameCallbackId = null;
        }
    }
    __dispose() {
        this.__stop();
        this.__removeOnDataStream();
        this._dataStreamCanvas = null;
        this._dataStreamCanvasContext = null;
    }
    _captureFrame(_now, metadata) {
        const camera = this._cameraHandler;
        if (camera.__getCurrentState !== STATE.__PLAY) {
            // this.__stop();
            // return;
        }
        if (this._onDataStreamCallback && (metadata?.presentedFrames || 0) % this._config.processingFrequency === 0) {
            this._capture(metadata);
        }
        this._requestVideoFrameCallbackId = camera.__getVideoElement.requestVideoFrameCallback((now, metadata) => this._captureFrame(now, metadata));
    }
    _capture(metadata) {
        const canvas = this._dataStreamCanvas;
        const ctx = this._dataStreamCanvasContext;
        if (!canvas || !ctx)
            return;
        const video = this._cameraHandler.__getVideoElement;
        const roi = this._computeROI();
        const outputSize = this._computeOutputSize(roi);
        // If resize is enabled, force exact target dimensions; otherwise use computed outputSize
        const targetWidth = this._config.resize ? this._config.resizeWidth : outputSize.width;
        const targetHeight = this._config.resize ? this._config.resizeHeight : outputSize.height;
        if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
            canvas.width = targetWidth;
            canvas.height = targetHeight;
        }
        ctx.drawImage(video, roi.x, roi.y, roi.width, roi.height, 0, 0, targetWidth, targetHeight);
        const imageData = ctx.getImageData(0, 0, targetWidth, targetHeight);
        this._onDataStreamCallback?.(this._requestVideoFrameCallbackId, imageData, metadata);
    }
    _computeROI() {
        const video = this._cameraHandler.__getVideoElement;
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;
        // ROI config coordinates are in CSS pixels relative to the video element's
        // displayed top-left corner (the ROI container shares the host origin with
        // the <video>). The video is rendered with `object-fit: cover`, so mapping
        // those CSS px to native video px must use the INVERSE cover transform, not
        // an independent per-axis fill scale.
        //
        // `object-fit: cover` scales the intrinsic frame by a single uniform factor
        // `coverScale = max(displayW/videoW, displayH/videoH)` so it fully covers the
        // element, then centers and crops the overflow. Therefore:
        //   coverScale = display px per native px (same on both axes)
        //   offset     = how many display px are cropped off each side (centered)
        //   native     = (displayCoord + offset) / coverScale
        //
        // Using a single scale also keeps a square on-screen box square in native px,
        // which is what the downstream detector (ONNX / QR) expects.
        const displayWidth = video.clientWidth || videoWidth;
        const displayHeight = video.clientHeight || videoHeight;
        const coverScale = Math.max(displayWidth / videoWidth, displayHeight / videoHeight);
        const offsetX = (videoWidth * coverScale - displayWidth) / 2;
        const offsetY = (videoHeight * coverScale - displayHeight) / 2;
        const { width, height, x, y } = this._config;
        // Mirror compensation: the <video> is CSS-flipped (`transform: scaleX(-1)`)
        // for the front/`user` camera — and also whenever only a single camera is
        // available — via the `data-facing-mode="user"` attribute. The captured
        // frame drawn by `drawImage` is NOT flipped, so the on-screen ROI x must be
        // mirrored before mapping to native pixels; otherwise an off-center ROI
        // (e.g. shifted to clear the controls panel) grabs the horizontally
        // opposite region. A centered ROI is unaffected (symmetric).
        const mirrored = video.dataset?.facingMode === 'user';
        const displayX = mirrored ? displayWidth - x - width : x;
        return {
            x: Math.round((displayX + offsetX) / coverScale),
            y: Math.round((y + offsetY) / coverScale),
            width: Math.round(width / coverScale),
            height: Math.round(height / coverScale),
            videoWidth,
            videoHeight,
        };
    }
    _computeOutputSize(roi) {
        const { minOutputSize } = this._config;
        const shorterSide = Math.min(roi.width, roi.height);
        // No upscaling needed
        if (shorterSide >= minOutputSize) {
            return { width: roi.width, height: roi.height };
        }
        // Upscale so shorter side = minOutputSize
        const scale = minOutputSize / shorterSide;
        return {
            width: Math.round(roi.width * scale),
            height: Math.round(roi.height * scale),
        };
    }
    _setupDataStreamCanvas() {
        if (this._dataStreamCanvas)
            return;
        this._dataStreamCanvas = document.createElement('canvas');
        this._dataStreamCanvasContext = this._dataStreamCanvas.getContext('2d');
    }
}
//# sourceMappingURL=video-data-stream.js.map